/**
 * Hot&Cold Section Builder
 * Creates one unified HOT_AND_COLD section from two existing sections (HOT_GAMES and COLD_GAMES) and replaces them in the list.
 *
 * As of the dynamic hot/cold endpoint rollout, the source HOT_GAMES / COLD_GAMES
 * sections in the main settings response come back with empty `games` arrays.
 * The actual list of games + their RTP values is delivered by a separate
 * endpoint (see `core/api/endpoints/hotCold.api.ts`) and passed in here.
 *
 * The minLimit / topLimit values still come from the source sections.
 */

import { HOT_AND_COLD_SECTION_NAME, SECTION_NAMES } from '@repo/common';
import type IGameSettings from 'types/models/IGameSettings';
import type ISectionSettings from 'types/models/ISectionSettings';
import { BANNER_TAG, setBannerTag, type HotColdBannerTag } from '../../games/constants/gameBanners';
import type { IHotColdGameEntry, IHotColdStatsResponse } from '../types/hotCold.types';

const applyTopLimit = (games: IGameSettings[], topLimit?: number) => {
  return topLimit && games.length >= topLimit ? games.slice(0, topLimit) : games;
};

/**
 * Build an IGameSettings instance for a hot/cold entry by cloning an existing
 * processed game from any section (the donor strategy from
 * lobby-settings-data-shapes), then overriding `statisticRtp` with the value
 * from the endpoint. Returns null if no donor is found — that game is silently
 * skipped (rare edge case where the game key exists in supportedGames but no
 * section currently surfaces it; refetching settings is not worth it for a
 * single missing card).
 */
const buildGameFromEntry = (
  entry: IHotColdGameEntry,
  existingSections: ISectionSettings[],
  status: HotColdBannerTag
): IGameSettings | null => {
  for (const section of existingSections) {
    const donor = section.games.find((g) => g.gameKey === entry.gameKey);
    if (donor) {
      // New object reference — Game.tsx uses reference-equality React.memo.
      return {
        ...donor,
        statisticRtp: entry.rtp,
        gameBanners: setBannerTag(donor.gameBanners, status, true),
      };
    }
  }
  return null;
};

export const createHotColdSection = (
  existingSections: ISectionSettings[],
  hotColdStats: IHotColdStatsResponse
): ISectionSettings | null => {
  const apiHotSection = existingSections.find((s) => s.name === SECTION_NAMES.HOT_GAMES);
  const apiColdSection = existingSections.find((s) => s.name === SECTION_NAMES.COLD_GAMES);

  if (!apiHotSection || !apiColdSection) {
    return null;
  }

  const hotMinLimit = apiHotSection.minLimit && apiHotSection.minLimit >= 1 ? apiHotSection.minLimit : 1;
  const coldMinLimit = apiColdSection.minLimit && apiColdSection.minLimit >= 1 ? apiColdSection.minLimit : 1;

  // minLimit is now gated against the dedicated endpoint's response, not the
  // (empty) source section arrays.
  if (hotColdStats.hotGames.length < hotMinLimit || hotColdStats.coldGames.length < coldMinLimit) {
    return null;
  }

  // Preserve the order returned by the endpoint (hot DESC by rtp, cold ASC).
  const hotGames = hotColdStats.hotGames
    .map((entry) => buildGameFromEntry(entry, existingSections, BANNER_TAG.HOT))
    .filter((g): g is IGameSettings => g !== null);
  const coldGames = hotColdStats.coldGames
    .map((entry) => buildGameFromEntry(entry, existingSections, BANNER_TAG.COLD))
    .filter((g): g is IGameSettings => g !== null);

  // Re-check after donor-lookup drop-outs: a malformed response could leave us
  // below minLimit even though the raw entry counts passed.
  if (hotGames.length < hotMinLimit || coldGames.length < coldMinLimit) {
    return null;
  }

  const limitedHotGames = applyTopLimit(hotGames, apiHotSection.topLimit);
  const limitedColdGames = applyTopLimit(coldGames, apiColdSection.topLimit);

  return {
    ...apiHotSection,
    name: HOT_AND_COLD_SECTION_NAME,
    icon: 'hot_and_cold_icon',
    games: limitedHotGames,
    hotColdSettings: {
      hotGames: limitedHotGames,
      coldGames: limitedColdGames,
      backgroundSourceName: SECTION_NAMES.HOT_GAMES,
    },
  };
};
